<template>
  <spinner v-if="initialLoading" />
  <div v-else class="container">
    <template v-if="currentStep === 1">
      <h1 class="container-title">{{ adminText('organizationAll.subscription_statistics_b226d6d4', 'Subscription statistics') }}</h1>
      <section class="mb-32 active-subscription-plans">
        <h2 class="page-container-subtitle">{{ adminText('organizationAll.active_a733b809', 'Active') }}</h2>
        <p v-if="!activeSubscriptionPlans.length" class="no-items-message">
          {{ adminText('organizationAll.no_active_subscription_plans_3b304fa1', 'No active subscription plans.') }}
        </p>
        <div v-else class="items-container">
          <article v-for="plan in activeSubscriptionPlans" :key="plan.id" class="item-card">
            <a href="#" class="item-link" @click.prevent="goToStatistics(plan)">
              <p class="item-title">{{ plan.name }}</p>
              <p class="item-date">{{ formatDate(plan.from) }} - {{ formatDate(plan.to) }}</p>
            </a>
          </article>
        </div>
      </section>
      <section>
        <h2 class="page-container-subtitle">{{ adminText('organizationAll.past_405c12fb', 'Past') }}</h2>
        <p v-if="!pastSubscriptionPlans.length" class="no-items-message">
          {{ adminText('organizationAll.no_past_subscription_plans_151fa5ca', 'No past subscription plans.') }}
        </p>
        <div v-else class="items-container">
          <article v-for="plan in pastSubscriptionPlans" :key="plan.id" class="item-card">
            <a href="#" class="item-link" @click.prevent="goToStatistics(plan)">
              <p class="item-title">{{ plan.name }}</p>
              <p class="item-date">{{ formatDate(plan.from) }} - {{ formatDate(plan.to) }}</p>
            </a>
          </article>
        </div>
      </section>
    </template>

    <template v-else>
      <main-button
        flat
        style="align-self: flex-start; padding-left: 0"
        @click="goBack"
      >
        <v-icon class="arrow-back">arrow_back</v-icon>{{ adminText('organizationAll.back_to_sport_subscriptions_b946df1e', 'Back to Sport subscriptions') }}
      </main-button>
      <h1 class="container-title">{{ adminText('organizationAll.subscription_sales_statistics_a565a742', 'Subscription sales statistics') }}</h1>
      <div class="filters-container">
        <select-field
          v-model="selectedSource"
          :items="organizationOptions(sources, 'name')"
          item-select="key"
          item-render="name"
          :label="adminText('organizationAll.source_6da13add', 'Source')"
          @change="handleFiltersChange"
        />
      </div>

      <div class="statistics-card-container">
        <div class="statistics-card">
          <div class="statistics-card-content">
            <span class="statistic-card-title">{{ adminText('organizationAll.total_tickets_sold_41fe7704', 'Total tickets sold') }}</span>
            <div class="total-sold-data">
              <span class="primary-data-style">{{ totalSubscriptionsSold }}</span>
            </div>
          </div>
          <img
            src="/assets/pie-chart.svg"
            :alt="adminText('organizationAll.pie_chart_icon_38e41208', 'Pie chart icon')"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>
        <div class="statistics-card">
          <div class="statistics-card-content">
            <span class="statistic-card-title">{{ adminText('organizationAll.total_income_450a9255', 'Total income') }}</span>
            <div class="total-sold-data">
              <span class="primary-data-style">{{ totalIncome }}&#8364;</span>
            </div>
          </div>
          <img
            src="/assets/pie-chart.svg"
            :alt="adminText('organizationAll.pie_chart_icon_38e41208', 'Pie chart icon')"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>
      </div>
      <div class="graphs-container">
        <graph
          :title="adminText('organizationAll.sale_of_subscriptions_per_day_24f22550', 'Sale of subscriptions per day')"
          :has-data="subscriptionsSoldByDate.labels.length !== 0"
          :data-filter="true"
          :initial-date-range-option="1"
          :date-filter-options="[1, 2, 3]"
          :max-date-range="21"
          @changeDateRange="changeSubscriptionsByDateRange($event)"
        >
          <stacked-bar-chart
            v-if="totalSubscriptionsSold !== 0"
            :data="organizationChartData(subscriptionsSoldByDate)"
            :options="{
              stackedY: true,
              barThickness: 22,
              barPercentage: 100,
              barDistance: 25,
            }"
          />
          <div v-else class="noDataText">
            {{ adminText('organizationAll.no_data_to_display_statistics_will_be_availab_654f6115', 'No data to display. Statistics will be available when someone buys a subscription.') }}
          </div>
          <div v-if="subscriptionsSoldByDate.labels.length === 0">
            {{ adminText('organizationAll.no_data_to_display_for_selected_time_period_0f6f64ed', 'No data to display for selected time period.') }}
          </div>
        </graph>
        <graph
          :title="adminText('organizationAll.sale_of_subscriptions_per_month_a13646c6', 'Sale of subscriptions per month')"
          :has-data="subscriptionsSoldByMonth.labels.length !== 0"
          :data-filter="true"
          :initial-date-range-option="1"
          :date-filter-options="[1, 2, 3]"
          :max-date-range="12"
          :range-type="DatePickerRangeType.MONTH"
          @changeDateRange="changeSubscriptionsByMonthRange($event)"
        >
          <stacked-bar-chart
            v-if="totalSubscriptionsSold !== 0"
            :data="organizationChartData(subscriptionsSoldByMonth)"
            :options="{
              stackedY: true,
              barThickness: 22,
              barPercentage: 100,
              barDistance: 25,
            }"
          />
          <div v-else class="noDataText">
            {{ adminText('organizationAll.no_data_to_display_statistics_will_be_availab_654f6115', 'No data to display. Statistics will be available when someone buys a subscription.') }}
          </div>
          <div v-if="subscriptionsSoldByMonth.labels.length === 0">
            {{ adminText('organizationAll.no_data_to_display_for_selected_time_period_0f6f64ed', 'No data to display for selected time period.') }}
          </div>
        </graph>
        <graph :title="adminText('organizationAll.subscriptions_ratio_34086d1b', 'Subscriptions ratio')" :data-filter="false" :has-data="true" :value="``">
          <pie-chart
            v-if="totalSubscriptionsSold !== 0"
            :data="organizationChartData(subscriptionsSoldByPrepaidCouponId)"
            :options="{ displayLegend: true }"
            legend-position="bottom"
          />
          <div v-else class="noDataText">
            {{ adminText('organizationAll.no_data_to_display_statistics_will_be_availab_654f6115', 'No data to display. Statistics will be available when someone buys a subscription.') }}
          </div>
        </graph>
        <graph
          :title="adminText('organizationAll.cumulative_income_bc56f288', 'Cumulative income')"
          data-filter
          has-data
          :value="``"
          @changeDateRange="changeCumulativeIncomeRange($event)"
        >
          <line-chart
            v-if="totalSubscriptionsSold !== 0 && cumulativeIncomeData.labels.length > 1"
            :data="organizationChartData(cumulativeIncomeData)"
            :options="{}"
          />
          <div v-else class="noDataText">
            <div v-if="cumulativeIncomeData.labels.length === 1">
              {{ adminText('organizationAll.in_the_selected_period_there_is_only_one_date_0342a94d', 'In the selected period, there is only one date with subscriptions sold:') }}<br>
              <b>
                {{ cumulativeIncomeData.labels[0] }}:
                {{ cumulativeIncomeData.datasets[0].data[0] }} {{ adminText('organizationAll.eur_d06e0731', 'EUR') }}
              </b>
            </div>
            <div v-else>
              {{ adminText('organizationAll.no_data_to_display_statistics_will_be_availab_654f6115', 'No data to display. Statistics will be available when someone buys a subscription.') }}
            </div>
          </div>
        </graph>
      </div>
    </template>
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { GET_SPORT_PREPAID_COUPONS } from '@/graphql/prepaidCoupon.graphql'
import {
  GET_PREPAID_COUPON_USERS_CUMULATIVE_INCOME_GROUPED_BY_CREATED_AT_DATE,
  GET_PREPAID_COUPON_USERS_GROUPED_BY_CREATED_AT_DATE,
  GET_PREPAID_COUPON_USERS_GROUPED_BY_CREATED_AT_MONTH,
  GET_PREPAID_COUPON_USERS_GROUPED_BY_PREPAID_COUPON_ID,
  GET_TOTAL_SPORT_PREPAID_COUPON_USERS_SOLD_DATA,
} from '@/graphql/prepaidCouponUser.graphql'
import StackedBarChart from '@/components/shared/graphs/stacked-bar-chart.vue'
import Graph from '@/components/shared/graphs/graph.vue'
import moment from 'moment'
import { chartBackgroundColors } from '@/utils'
import PieChart from '@/components/shared/graphs/pie-chart.vue'
import { DatePickerRangeType } from '@/types'
import LineChart from '@/components/shared/graphs/line-chart.vue'

export default {
  mixins: [OrganizationLocalization],
  name: 'sport-subscriptions-statistics',
  components: { LineChart, PieChart, Graph, StackedBarChart },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      subscriptionPlans: [],
      activeSubscriptionPlans: [],
      pastSubscriptionPlans: [],
      selectedSubscriptionPlan: null,
      selectedPlanName: null,
      totalSubscriptionsSold: 0,
      totalIncome: 0,
      subscriptionsSoldByDate: [],
      initialLoading: true,
      subscriptionsSoldByMonth: [],
      subscriptionsSoldByPrepaidCouponId: [],
      cumulativeIncomeData: [],
      currentStep: 1,
      soldSubscriptionsByDateRange: [
        moment().subtract(6, 'day').startOf('day'),
        moment().endOf('day'),
      ],
      soldSubscriptionsByMonthRange: [
        moment().subtract(5, 'month').startOf('month'),
        moment().endOf('month'),
      ],
      cumulativeIncomeRange: [moment().subtract(6, 'day').startOf('day'), moment().endOf('day')],
      sources: [
        { key: null, name: 'All sources' },
        { key: 'web', name: 'Web' },
        { key: 'import', name: 'Import' },
      ],
      selectedSource: null,
    }
  },
  computed: {
    DatePickerRangeType() {
      return DatePickerRangeType
    },
  },
  async mounted() {
    await this.fetchSubscriptionPlans()
    this.initialLoading = false
  },
  methods: {
    async goToStatistics(plan) {
      this.selectedSubscriptionPlan = plan.id
      this.selectedPlanName = plan.name
      this.currentStep = 2
      this.initialLoading = true
      await this.fetchAllData()
      this.initialLoading = false
    },
    async fetchAllData() {
      await Promise.all([
        this.fetchSubscriptionPlans(),
        this.fetchTotalSoldSubscriptionsData(),
        this.fetchSoldSubscriptionsByDate(),
        this.fetchSoldSubscriptionsByMonth(),
        this.fetchCumulativeIncome(),
      ])
      // next fetching cannot be done in parallel because it uses variables initialized in previous queries
      await this.fetchSoldSubscriptionsByPrepaidCouponId()
    },
    async fetchSubscriptionPlans() {
      const {
        data: { getSportOrganizationPrepaidCoupons },
        errors,
      } = await this.$apollo.query({
        query: GET_SPORT_PREPAID_COUPONS,
        variables: { organizationSlug: this.organizationSlug },
      })

      if (errors) return

      this.subscriptionPlans = getSportOrganizationPrepaidCoupons
      this.activeSubscriptionPlans = this.subscriptionPlans.filter(plan =>
        moment(plan.to).isAfter(moment()),
      )
      this.pastSubscriptionPlans = this.subscriptionPlans.filter(plan =>
        moment(plan.to).isBefore(moment()),
      )
    },
    async fetchTotalSoldSubscriptionsData() {
      const {
        data: { getTotalSportPrepaidCouponUsersSoldData },
        errors,
      } = await this.$apollo.query({
        query: GET_TOTAL_SPORT_PREPAID_COUPON_USERS_SOLD_DATA,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponId: this.selectedSubscriptionPlan,
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      const { soldItems, totalIncome } = getTotalSportPrepaidCouponUsersSoldData
      this.totalSubscriptionsSold = soldItems
      this.totalIncome = totalIncome
    },
    async fetchSoldSubscriptionsByDate() {
      const {
        data: { getPrepaidCouponUsersGroupedByCreatedAtDate: dateGroups },
        errors,
      } = await this.$apollo.query({
        query: GET_PREPAID_COUPON_USERS_GROUPED_BY_CREATED_AT_DATE,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponId: this.selectedSubscriptionPlan,
          startDate: this.soldSubscriptionsByDateRange[0],
          endDate: this.soldSubscriptionsByDateRange[1],
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      this.subscriptionsSoldByDate = {
        labels: dateGroups.map(group => moment(group.date).format('D/M')),
        datasets: [
          {
            label: 'Sold subscriptions per day',
            data: dateGroups.map(group => group.count),
            bgColor: '#CEE4F2',
            borderColor: '#CEE4F2',
            tooltipLabels: dateGroups.map(group => {
              return {
                label: `${moment(group.date).format('D.M')}|${group.count}`,
              }
            }),
          },
        ],
      }
    },
    async fetchSoldSubscriptionsByMonth() {
      const {
        data: { getPrepaidCouponUsersGroupedByCreatedAtMonth: monthGroups },
        errors,
      } = await this.$apollo.query({
        query: GET_PREPAID_COUPON_USERS_GROUPED_BY_CREATED_AT_MONTH,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponId: this.selectedSubscriptionPlan,
          startDate: this.soldSubscriptionsByMonthRange[0],
          endDate: this.soldSubscriptionsByMonthRange[1],
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      this.subscriptionsSoldByMonth = {
        labels: monthGroups.map(group => moment(group.month).format('YY\'/M')),
        datasets: [
          {
            label: 'Sold subscriptions per month',
            data: monthGroups.map(group => group.count),
            bgColor: '#CEE4F2',
            borderColor: '#CEE4F2',
            tooltipLabels: monthGroups.map(group => {
              return {
                label: `${moment(group.month).format('YY\'/M')}|${group.count}`,
              }
            }),
          },
        ],
      }
    },
    async fetchSoldSubscriptionsByPrepaidCouponId() {
      const {
        data: { getPrepaidCouponUsersGroupedByPrepaidCouponId: subscriptionGroups },
        errors,
      } = await this.$apollo.query({
        query: GET_PREPAID_COUPON_USERS_GROUPED_BY_PREPAID_COUPON_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponId: this.selectedSubscriptionPlan,
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      const labels = subscriptionGroups.map(group => {
        const subscriptionPlan = this.subscriptionPlans.find(
          plan => plan.id === group.prepaidCouponId,
        )

        return subscriptionPlan.name || ''
      })
      this.subscriptionsSoldByPrepaidCouponId = {
        labels,
        datasets: [
          {
            label: 'Sold subscriptions per subscription type',
            data: subscriptionGroups.map(group => group.count),
            backgroundColor: chartBackgroundColors,
            hoverBackgroundColor: chartBackgroundColors,
          },
        ],
      }
    },

    async fetchCumulativeIncome() {
      const {
        data: { getPrepaidCouponUsersCumulativeIncomeGroupedByCreatedAtDate: dateGroups },
        errors,
      } = await this.$apollo.query({
        query: GET_PREPAID_COUPON_USERS_CUMULATIVE_INCOME_GROUPED_BY_CREATED_AT_DATE,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponId: this.selectedSubscriptionPlan,
          startDate: this.cumulativeIncomeRange[0],
          endDate: this.cumulativeIncomeRange[1],
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      this.cumulativeIncomeData = {
        labels: dateGroups.map(group => moment(group.date).format('D/M')),
        datasets: [
          {
            label: 'Cumulative Income',
            data: dateGroups.map(group => group.earnings),
            tooltipLabels: dateGroups.map(
              group => `${moment(group.date).format('D.M')}|${group.earnings} EUR`,
            ),
          },
        ],
      }
    },
    async changeSubscriptionsByDateRange(value) {
      if (value.isCustom) {
        this.soldSubscriptionsByDateRange = [
          moment(value.dateRange[0]).startOf('day'),
          moment(value.dateRange[1]).endOf('day'),
        ]
      } else {
        this.soldSubscriptionsByDateRange = value.dateRange
      }
      await this.fetchSoldSubscriptionsByDate()
    },
    async changeSubscriptionsByMonthRange(value) {
      if (value.isCustom) {
        this.soldSubscriptionsByMonthRange = [
          moment(value.dateRange[0]).startOf('month'),
          moment(value.dateRange[1]).endOf('month'),
        ]
      } else {
        this.soldSubscriptionsByMonthRange = value.dateRange
      }
      await this.fetchSoldSubscriptionsByMonth()
    },
    async changeCumulativeIncomeRange(value) {
      if (value.isCustom) {
        this.cumulativeIncomeRange = [
          moment(value.dateRange[0]).startOf('day'),
          moment(value.dateRange[1]).endOf('day'),
        ]
      } else {
        this.cumulativeIncomeRange = value.dateRange
      }
      await this.fetchCumulativeIncome()
    },
    async handleFiltersChange() {
      await this.fetchAllData()
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY.')
    },
    goBack() {
      this.currentStep = 1
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 70rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.container-title {
  color: #ff4c00;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.045rem;
}

.statistics-card-container {
  display: flex;
  padding: 1rem;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: -8px;

  border-radius: 1rem;
  background: #f5f5f5;
}
.statistics-card {
  display: flex;
  padding: 1rem;
  gap: 1rem;
  justify-content: space-between;
  align-items: flex-end;
  flex: 1 0 0;

  border-radius: 0.5rem;
  border: 1px solid #fff;
  background: #fff;
}
.statistics-card:hover {
  border: 1px solid #d9d9d9;
  box-shadow: 0 4px 16px 0 rgba(51, 51, 51, 0.16);
}
.statistic-card-title {
  color: #666;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  white-space: nowrap;
}
.statistics-card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}
.total-sold-data {
  display: flex;
  align-items: baseline;
}
.primary-data-style {
  color: #0277bd;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 2.25rem;
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
  letter-spacing: -0.0675rem;
  white-space: nowrap;
  @media screen and (max-width: 860px) {
    font-size: 1.75rem;
  }
}
.secondary-small-data-style {
  color: #666;
  font-variant-numeric: lining-nums proportional-nums;

  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  white-space: nowrap;
  @media screen and (max-width: 860px) {
    font-size: 0.75rem;
  }
}
.total-earning-data {
  display: flex;
  align-items: baseline;
  gap: 1rem;
}

.tertiary-data-style {
  color: #ff8875;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 2.25rem;
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
  letter-spacing: -0.0675rem;
  white-space: nowrap;
  @media screen and (max-width: 860px) {
    font-size: 1.75rem;
  }
}
@media (max-width: 800px) {
  .statistics-card-container {
    flex-direction: column;
  }
  .statistics-card {
    margin: 12px auto !important;
    width: 100% !important;
  }
}

.graphs-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(415px, 1fr));
  gap: 1.5rem;
}

@media screen and (max-width: 450px) {
  .graphs-container {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}

.noDataText {
  height: 175px;
  margin-top: 150px;
  color: grey;
}

.filters-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media screen and (max-width: 500px) {
  .filters-container {
    grid-template-columns: 1fr;
  }
}

/* Selection step styles (matching overview design) */
.page-container-subtitle {
  color: #ff4c00;
  font-size: 20px;
  font-family: Raleway;
  font-weight: 600;
  line-height: 24px;
  word-wrap: break-word;
  margin-bottom: 0.75rem;
}

.items-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 1150px) {
  .items-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

.no-items-message {
  color: #666666;
  font-size: 18px;
  font-family: Source Sans Pro;
  font-weight: 400;
  line-height: 27px;
  word-wrap: break-word;
}

.item-card {
  background-color: #f5f5f5;
  border-radius: 1.5rem;
}

.item-link {
  display: block;
  padding: 2rem;
  text-decoration: none;
  color: inherit;
}

.item-title {
  margin: 0;
  color: #666666;
  font-size: 24px;
  font-family: Raleway;
  font-weight: 600;
  line-height: 28.8px;
  word-wrap: break-word;
}

.active-subscription-plans .item-title {
  color: #0277bd;
}

.item-date {
  margin: 0;
  color: #666666;
  font-size: 18px;
  font-family: Source Sans Pro;
  font-weight: 400;
  line-height: 27px;
  word-wrap: break-word;
}
</style>
